'use client'

import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useToaster } from '../context/ToasterContext';
import Spinner from "../components/Spinner";
import Button from '../components/Button';
import Table from '../components/Table';
import ConfirmationModal from '../components/ConfirmationModal';
import { useDispatch, useSelector } from 'react-redux';
import { fetchConnectors } from '../redux/globalDataSlice';
import api from '../api/axios';

const INPUT_CLS = 'rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-1.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500';
const EMPTY_FILTERS = { connectorName: '', connectorApi: '', connectorType: '' };

export default function Connectors() {
  const { toaster } = useToaster();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const { connectors, dataLoading } = useSelector((state) => state.globalData);
  const activeAccountId = useSelector((s) => s.globalData.activeAccountId);

  useEffect(() => { setLoading(dataLoading); }, [dataLoading]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const hasFilters = Object.values(filters).some(Boolean);

  const filteredAndSorted = useMemo(() => {
    let result = [...(connectors || [])];

    if (filters.connectorName) {
      result = result.filter(c =>
        c.connectorName?.toLowerCase().includes(filters.connectorName.toLowerCase())
      );
    }
    if (filters.connectorApi) {
      result = result.filter(c => c.connectorApi === filters.connectorApi);
    }
    if (filters.connectorType) {
      result = result.filter(c => c.connectorType === filters.connectorType);
    }

    if (sortConfig.key) {
      result.sort((a, b) => {
        const aVal = String(a[sortConfig.key] ?? '');
        const bVal = String(b[sortConfig.key] ?? '');
        const cmp = aVal.localeCompare(bVal);
        return sortConfig.direction === 'asc' ? cmp : -cmp;
      });
    }

    return result;
  }, [connectors, filters, sortConfig]);

  const initDeleteConnector = (itemData) => {
    setShowConfirmationModal(true);
    setSelectedItem(itemData);
  };

  const deleteConnector = async () => {
    try {
      await api.delete(`${process.env.REACT_APP_BASE_API_URL}/mvapi/connectors/account/${activeAccountId}/${selectedItem.id}`);
      toaster.success('Connector deleted successfully!');
      if (activeAccountId) dispatch(fetchConnectors(activeAccountId));
      setShowConfirmationModal(false);
    } catch (error) {
      console.error('Error:', error);
      toaster.error('Something went wrong. Please try again.');
    }
  };

  return (
    <>
      {loading && <Spinner />}

      {!loading && (
        <>
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="sm:flex sm:items-center">
              <div className="sm:flex-auto">
                <h2 className="text-2xl/7 font-bold text-gray-900 dark:text-gray-100 sm:truncate sm:text-3xl sm:tracking-tight">
                  Connectors
                </h2>
                <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                  Manage all your connectors here
                </p>
              </div>
              <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
                <Button type="button" variant="primary" onClick={() => navigate('/add-connector')}>
                  Create new connector
                </Button>
              </div>
            </div>

            {/* Filter bar */}
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <input
                type="text"
                placeholder="Filter by name…"
                value={filters.connectorName}
                onChange={e => setFilters(f => ({ ...f, connectorName: e.target.value }))}
                className={INPUT_CLS}
              />
              <select
                value={filters.connectorApi}
                onChange={e => setFilters(f => ({ ...f, connectorApi: e.target.value }))}
                className={INPUT_CLS}
              >
                <option value="">All APIs</option>
                <option value="SMS">SMS</option>
                <option value="MESSAGES">Messages</option>
              </select>
              <select
                value={filters.connectorType}
                onChange={e => setFilters(f => ({ ...f, connectorType: e.target.value }))}
                className={INPUT_CLS}
              >
                <option value="">All types</option>
                <option value="VONAGE">Vonage</option>
                <option value="TWILIO">Twilio</option>
              </select>
              {hasFilters && (
                <button
                  type="button"
                  onClick={() => setFilters(EMPTY_FILTERS)}
                  className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline"
                >
                  Clear filters
                </button>
              )}
            </div>

            <div className="mt-4">
              <Table
                data={filteredAndSorted}
                sortConfig={sortConfig}
                onSort={handleSort}
                onRowClick={(connector) => navigate(`/edit-connector/${connector.id}`)}
                columns={[
                  { key: 'connectorName', title: 'Connector name', sortable: true },
                  { key: 'username', title: 'Username', sortable: true },
                  { key: 'connectorApi', title: 'Connector API', sortable: true },
                  { key: 'connectorType', title: 'Connector type', sortable: true },
                  {
                    key: 'actions',
                    title: 'Actions',
                    align: 'right',
                    render: (connector) => (
                      <div className="space-x-2">
                        <Link
                          to={`/edit-connector/${connector.id}`}
                          className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-900 dark:hover:text-emerald-100 mr-5"
                        >
                          Edit
                        </Link>
                        <button
                          type="button"
                          onClick={() => initDeleteConnector(connector)}
                          className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-100"
                        >
                          Delete
                        </button>
                      </div>
                    ),
                  },
                ]}
                emptyState={{
                  title: hasFilters ? 'No connectors match your filters' : 'No connectors found',
                  description: hasFilters ? 'Try adjusting or clearing the filters.' : 'Get started by creating a new connector.',
                }}
              />
            </div>
          </div>
        </>
      )}

      <ConfirmationModal
        open={showConfirmationModal}
        title="Delete connector"
        description="Are you sure you want to delete? This action cannot be undone."
        confirmText="Yes"
        cancelText="No"
        onConfirm={deleteConnector}
        onCancel={() => setShowConfirmationModal(false)}
      />
    </>
  );
}
